import {
  APIEntityType,
  ArdoqId,
  BooleanOperator,
  isQueryBuilderRule,
  isQueryBuilderSubquery,
  Operator,
  QueryBuilderQuery,
  QueryBuilderRow,
  QueryBuilderRule,
  QueryBuilderSubquery,
  SearchContext,
} from '@ardoq/api-types';
import { formatDateISO } from '@ardoq/date-time';
import { DEFAULT_FILTERS_BY_SEARCH_CONTEXT } from './filterUtils/const';
import { uniq } from 'lodash';

const getEmptyQueryWithComponentContext = (): QueryBuilderQuery => ({
  condition: BooleanOperator.AND,
  rules: [
    {
      field: 'type',
      id: 'type',
      input: 'select',
      operator: Operator.EQUAL,
      type: 'string',
      value: SearchContext.COMPONENT,
    },
    {
      condition: BooleanOperator.AND,
      rules: [],
    },
  ],
});

const getRootWorkspaceQueryRule = (workspaceId: ArdoqId): QueryBuilderRule => {
  return {
    id: 'rootWorkspace',
    field: 'rootWorkspace',
    input: 'text',
    operator: Operator.EQUAL,
    type: 'string',
    value: workspaceId,
  };
};

const getTargetWorkspaceQueryRule = (
  workspaceId: ArdoqId
): QueryBuilderRule => ({
  id: 'targetWorkspace',
  field: 'targetWorkspace',
  input: 'text',
  operator: Operator.EQUAL,
  type: 'string',
  value: workspaceId,
});

const getComponentTypeQueryRule = (
  componentTypeName: string
): QueryBuilderRule => {
  return {
    field: 'typeName',
    id: 'typeName',
    input: 'text',
    operator: Operator.EQUAL,
    type: 'string',
    value: componentTypeName,
  };
};

const getReferenceSourceOrTargetComponentTypeQueryRule = (
  field: 'ardoq.sourceComponentTypeName' | 'ardoq.targetComponentTypeName',
  componentTypeName: string
): QueryBuilderRule => ({
  id: field,
  field,
  input: 'text',
  operator: Operator.EQUAL,
  type: 'string',
  value: componentTypeName,
});

const getComponentIdsQueryRule = (
  componentIds: ArdoqId[]
): QueryBuilderRule => {
  return {
    field: '_id',
    id: '_id',
    input: 'text',
    operator: Operator.IN,
    type: 'string',
    value: componentIds,
  };
};

const getEmptyParentComponentQueryRule = (): QueryBuilderRule => {
  return {
    id: 'parent',
    field: 'parent',
    input: 'text',
    type: 'string',
    operator: Operator.IS_EMPTY,
    value: null,
    targetEntityType: APIEntityType.COMPONENT,
  };
};

const getParentComponentQueryRule = (
  parentComponentId: ArdoqId
): QueryBuilderRule => {
  return {
    id: 'parent',
    field: 'parent',
    input: 'text',
    type: 'string',
    operator: Operator.EQUAL,
    value: parentComponentId,
    targetEntityType: APIEntityType.COMPONENT,
  };
};

const getQueryRuleForParentComponentOrEmptyParent = (
  parentComponentId: ArdoqId | null
): QueryBuilderRule => {
  if (parentComponentId === null) {
    return getEmptyParentComponentQueryRule();
  }
  return getParentComponentQueryRule(parentComponentId);
};

const getCheckboxFieldQueryRule = (
  fieldName: string,
  value: boolean
): QueryBuilderRule => {
  return {
    field: fieldName,
    id: fieldName,
    input: 'select',
    operator: Operator.EQUAL,
    type: 'boolean',
    value: value,
  };
};

const getFieldIsNotEmptyQueryRule = (fieldName: string): QueryBuilderRule => {
  return {
    field: fieldName,
    id: fieldName,
    input: 'text',
    operator: Operator.IS_NOT_EMPTY,
    type: 'string',
    value: null,
  };
};

const getFieldIsEmptyQueryRule = (fieldName: string): QueryBuilderRule => {
  return {
    field: fieldName,
    id: fieldName,
    input: 'text',
    operator: Operator.IS_EMPTY,
    type: 'string',
    value: null,
  };
};

const getLastUpdatedBeforeQueryRule = (date: Date): QueryBuilderRule => {
  return {
    id: 'last-updated',
    field: 'last-updated',
    input: 'text',
    operator: Operator.LESS,
    type: 'date',
    value: formatDateISO(date),
  };
};

const addSubqueryToComponentQuery = (
  subquery: QueryBuilderSubquery
): QueryBuilderQuery => {
  return {
    condition: BooleanOperator.AND,
    rules: [
      {
        id: 'type',
        field: 'type',
        type: 'string',
        input: 'select',
        operator: Operator.EQUAL,
        value: SearchContext.COMPONENT,
      },
      subquery,
    ],
  };
};

const getSubqueryFromQuery = (query: QueryBuilderQuery): QueryBuilderSubquery =>
  query.rules[1];

const filterOutEmptyRulesFromQuery = (
  query: QueryBuilderQuery
): QueryBuilderQuery => {
  const filterRules = (rules: QueryBuilderRow[]): QueryBuilderRow[] => {
    return rules.filter(rule => {
      if (isQueryBuilderRule(rule)) {
        return true;
      }
      if (isQueryBuilderSubquery(rule)) {
        rule.rules = filterRules(rule.rules);
        return rule.rules.length > 0;
      }
      return false;
    });
  };

  const [searchContextRule, subquery] = query.rules;

  return {
    condition: query.condition,
    rules: [
      searchContextRule,
      { condition: subquery.condition, rules: filterRules(subquery.rules) },
    ],
  };
};

const getAndSubqueryFromWithRules = (
  rules: QueryBuilderRow[]
): QueryBuilderSubquery => {
  return {
    condition: BooleanOperator.AND,
    rules,
  };
};

/**
 * Recursively collects field names from a QueryBuilderSubquery or QueryBuilderRule.
 * If the field is not a key of APIComponentAttributes, replaces it with "custom field".
 */
const getFieldNamesFromSubquery = (
  subquery: QueryBuilderSubquery | QueryBuilderRule
): string[] => {
  const defaultFilterFieldIds = new Set(
    DEFAULT_FILTERS_BY_SEARCH_CONTEXT[SearchContext.COMPONENT].map(f => f.id)
  );

  const collect = (row: QueryBuilderRow): string[] => {
    if (isQueryBuilderRule(row)) {
      if (defaultFilterFieldIds.has(row.field)) {
        return [row.field];
      }
      return ['custom field'];
    }
    if (isQueryBuilderSubquery(row)) {
      return row.rules.flatMap(collect);
    }
    return [];
  };

  return uniq(collect(subquery));
};

/**
 * This function can be useful for tracking the number of rules in a subquery.
 */
const calculateNumberOfRulesInSubQuery = (
  query: QueryBuilderSubquery | QueryBuilderRule
): number => {
  if (isQueryBuilderSubquery(query)) {
    return query.rules.reduce(
      (acc, rule) => acc + calculateNumberOfRulesInSubQuery(rule),
      0
    );
  }
  return 1;
};

const isEmptyQuery = (query: QueryBuilderQuery): boolean =>
  calculateNumberOfRulesInSubQuery(getSubqueryFromQuery(query)) === 0;

/**
 * Set of helper function for building adv. search based payloads outside of query builder
 * Currently used in: metamodel analytics adv. search table
 * // TODO use these functions in report references filter
 */
export const queryBuilderOperations = {
  getEmptyQueryWithComponentContext,
  getRootWorkspaceQueryRule,
  getTargetWorkspaceQueryRule,
  getComponentTypeQueryRule,
  getReferenceSourceOrTargetComponentTypeQueryRule,
  getComponentIdsQueryRule,
  getCheckboxFieldQueryRule,
  getFieldIsNotEmptyQueryRule,
  getFieldIsEmptyQueryRule,
  getLastUpdatedBeforeQueryRule,
  addSubqueryToComponentQuery,
  getSubqueryFromQuery,
  filterOutEmptyRulesFromQuery,
  getAndSubqueryFromWithRules,
  getFieldNamesFromSubquery,
  calculateNumberOfRulesInSubQuery,
  isEmptyQuery,
  getQueryRuleForParentComponentOrEmptyParent,
};
